import {
  Button,
  Form,
  Input,
  Select,
  Table,
  DatePicker,
  Space,
  InputRef,
} from "antd";
import { useState, useEffect, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ColumnsType } from "antd/es/table";
import type { FilterDropdownProps } from "antd/es/table/interface";
import type { ColumnType } from "antd/es/table/interface";
import { BuyUpQueryDataType } from "./types";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { ServiceTypes } from "../../../types/types";
import dayjs from "dayjs";
import ContentTitle from "../../../components/ui/ContentTitle";
import { useUpgradePaymentListQuery } from "../../../graphql/operations/clubApp/buyUp/get-buy-up-list";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import "./BuyUpQuery.scss";

interface BuyUpQueryProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}
const BuyUpQuery = ({
  setSelectedService,
  setSelectSubItem,
}: BuyUpQueryProps) => {
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();
  const [dataDisplay, setDataDisplay] = useState<BuyUpQueryDataType[]>([]);
  const [showDownload, setShowDownload] = useState(false);
  const [market, setMarket] = useState("");
  const [showData, setShowData] = useState(false);
  const [memberNumber, setMemberNumber] = useState("");
  const [paymentStartDate, setPaymentStartDate] = useState("");
  const [paymentEndDate, setPaymentEndDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const currentUser = useReactiveVar(__currentUser__);
  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 2,
      },
    });
  const { data, refetch } = useUpgradePaymentListQuery({
    variables: {
      market: market,
      memberNumber: memberNumber,
      paymentStartDate: paymentStartDate,
      paymentEndDate: paymentEndDate,
    },
  });

  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: any,
    confirm: Function,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearchText("");
  };

  const handleSubmit = () => {
    const { memberNumber, paymentDate } = form.getFieldsValue();
    const startDate = paymentDate
      ? paymentDate[0].format("YYYY-MM-DD")
      : undefined;
    const endDate = paymentDate
      ? paymentDate[1].format("YYYY-MM-DD")
      : undefined;
    console.log(memberNumber, startDate, endDate);
    setPaymentStartDate(startDate);
    setPaymentEndDate(endDate);
    setMemberNumber(memberNumber);
    setShowData(true);
    refetch();
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] as string}
          onChange={(e) =>
            setSelectedKeys(
              e.target.value ? [e.target.value] : ([] as string[])
            )
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0] as string);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button type="link" size="small" onClick={close}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(value.toString().toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleChange = (value: string) => {
    setMarket(value);
  };

  const handleDownload = () => {
    if (!dataDisplay || dataDisplay.length === 0) {
      console.error("No data to export");
      return;
    }

    const formattedData = dataDisplay.map(
      ({
        __typename,
        memberNo,
        memberName,
        addDongle,
        addBeamProjector,
        isChangeAddress,
        ...rest
      }) => ({
        memberNo,
        memberName,
        addDongle: addDongle === 1 ? "Yes" : "No",
        addBeamProjector: addBeamProjector === 1 ? "Yes" : "No",
        isChangeAddress: isChangeAddress ? "Yes" : "No",
        ...rest,
      })
    );

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(
      dataBlob,
      `Buy-up-list-${dayjs().format("YYYY-MM-DD")}.xlsx`
    );
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Buy Up Query");
  }, []);
  useEffect(() => {
    if (currentUser?.afUserMarkets && currentUser?.afUserMarkets[0]?.market) {
      if (currentUser?.afUserMarkets.length === 1) {
        setMarket(currentUser?.afUserMarkets[0]?.market);
        form.setFieldValue("market", currentUser?.afUserMarkets[0]?.market);
      } else {
        setMarket("KR");
      }
    }
  }, [currentUser]);
  useEffect(() => {
    if (data?.upgradePaymentList) {
      setDataDisplay(data.upgradePaymentList);
    }
  }, [data]);

  const columns: ColumnsType<BuyUpQueryDataType> = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
    },
    {
      title: "Member Name",
      dataIndex: "memberName",
      key: "memberName",
      width: 100,
      ...getColumnSearchProps("memberName"),
    },
    {
      title: "Member Number",
      dataIndex: "memberNo",
      key: "memberNo",
      width: 100,
      ...getColumnSearchProps("memberNo"),
    },
    {
      title: "Original Package",
      dataIndex: "originalPackage",
      key: "originalPackage",
      filters: [
        { text: "Original", value: "Original" },
        { text: "MTDGSAQLP", value: "MTDGSAQLP" },
        { text: "MTDGSP", value: "MTDGSP" },
        { text: "MTDSP", value: "MTDSP" },
        { text: "TDSP", value: "TDSP" },
        { text: "DSP", value: "DSP" },
      ],
      onFilter: (value, record) => {
        if (
          !record.originalPackage ||
          typeof record.originalPackage !== "string"
        ) {
          return false;
        }

        if (typeof value !== "string") {
          return false;
        }

        const sortedValue = value.split("").sort().join("");
        const sortedRecord = record.originalPackage.split("").sort().join("");

        return sortedRecord === sortedValue;
      },
      width: 100,
    },
    {
      title: "Upgrade Package",
      dataIndex: "upgradePackage",
      key: "upgradePackage",
      filters: [
        { text: "MTDSGAQLP", value: "MTDSGAQLP" },
        { text: "MTDSGP", value: "MTDSGP" },
        { text: "MTDSP", value: "MTDSP" },
      ],
      onFilter: (value, record) => {
        if (
          !record.upgradePackage ||
          typeof record.upgradePackage !== "string"
        ) {
          return false;
        }

        if (typeof value !== "string") {
          return false;
        }

        const sortedValue = value.split("").sort().join("");
        const sortedRecord = record.upgradePackage.split("").sort().join("");

        return sortedRecord === sortedValue;
      },
      width: 100,
    },
    {
      title: "Add Beam Projector",
      dataIndex: "addBeamProjector",
      width: 100,
      key: "addBeamProjector",
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      onFilter: (value, record) => record.addBeamProjector === value,
      render: (v, _) => (v === 1 ? "Yes" : "No"),
    },
    {
      title: "Add Dongle",
      dataIndex: "addDongle",
      width: 100,
      key: "addDongle",
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      onFilter: (value, record) => record.addDongle === value,
      render: (v, _) => (v === 1 ? "Yes" : "No"),
    },
    {
      title: "Payment Amount",
      dataIndex: "paymentAmount",
      key: "paymentAmount",
      width: 100,
      sorter: (a, b) => a.paymentAmount - b.paymentAmount,
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 100,
      sorter: (a, b) => a.paidAmount - b.paidAmount,
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      filters: [
        { text: "canceled", value: "canceled" },
        { text: "completed", value: "completed" },
        { text: "pending", value: "pending" },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      sorter: (a, b) => {
        if (!a.paymentDate) return 1;

        if (!b.paymentDate) return -1;

        const aTime = dayjs(a.paymentDate).valueOf();
        const bTime = dayjs(b.paymentDate).valueOf();
        return aTime - bTime;
      },
      render: (v, _) => (v ? dayjs(v).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Canceled Date",
      dataIndex: "canceledDate",
      key: "canceledDate",
      sorter: (a, b) => {
        if (!a.canceledDate) return 1;

        if (!b.canceledDate) return -1;

        const aTime = dayjs(a.canceledDate).valueOf();
        const bTime = dayjs(b.canceledDate).valueOf();
        return aTime - bTime;
      },
      render: (v, _) => (v ? dayjs(v).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Change Address",
      dataIndex: "isChangeAddress",
      key: "isChangeAddress",
      width: 100,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.isChangeAddress === value,
      render: (v, _) => (v ? "Yes" : "No"),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      sorter: (a, b) => {
        if (!a.createdDate) return 1;

        if (!b.createdDate) return -1;

        const aTime = dayjs(a.createdDate).valueOf();
        const bTime = dayjs(b.createdDate).valueOf();
        return aTime - bTime;
      },
      render: (v, _) => (v ? dayjs(v).format("YYYY-MM-DD") : "N/A"),
    },
  ];

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Buy Up Query"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="buyUpQueryWrapper">
      <ContentTitle title="Buy Up Query" />
      <div className="table-container">
        {currentUser?.afUserMarkets?.length &&
          currentUser?.afUserMarkets?.length > 1 && (
            <div className="flex justify-end p-5">
              <div>
                <span>Market:</span>
                <Select
                  onChange={handleChange}
                  defaultValue={market}
                  options={[
                    {
                      value: "TW",
                      label: "TW",
                    },
                    {
                      value: "HK",
                      label: "HK",
                    },
                    {
                      value: "KR",
                      label: "KR",
                    },
                  ]}
                />
              </div>
            </div>
          )}
        <Form form={form} name="buy-up-query-form" className="flex gap-10">
          <Form.Item
            label="Member Number"
            name="memberNumber"
            className="w-4/12"
          >
            <Input />
          </Form.Item>
          <Form.Item label="Payment Date" name="paymentDate" className="w-4/12">
            <RangePicker />
          </Form.Item>

          <div className="flex">
            <Button
              type="primary"
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    handleSubmit();
                  })
                  .catch((e) => console.log(e));
                setShowDownload(true);
              }}
            >
              Query
            </Button>
            <Button
              className="ms-2"
              onClick={() => {
                form.resetFields();
              }}
            >
              Clear
            </Button>
            {dataDisplay.length > 0 && showDownload && (
              <Button className="ms-2" onClick={handleDownload}>
                Download
              </Button>
            )}
          </div>
        </Form>
      </div>
      {showData && (
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={dataDisplay}
            scroll={{ x: "max-content" }}
          />
        </div>
      )}
    </div>
  );
};

export default BuyUpQuery;
